/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  HOMEPAGE_BLOCK_IDS,
  type HomepageBlock,
  type SpotlightCardsBlock,
  type ProductTilesBlock,
  type PrimeAdsBlock,
  type NotificationBannerBlock,
  type RecommendationTabsBlock,
  type CookiesBannerBlock,
  type LoginBannerBlock,
  type HeroBannerBlock,
  type ArticleCarouselBlock,
  type FooterAdsBannerBlock,
  type LobSearchTabsBlock,
  type BottomNavigationBlock,
} from './homepageBlockTypes';

/**
 * Factory for creating type guards using centralized constants.
 */
const createTypeGuard = <T extends HomepageBlock>(id: string) => {
  return (block: HomepageBlock): block is T => block.id === id;
};

// --- Optimized Type Guards using Constants ---
export const isSpotlightCardsBlock = createTypeGuard<SpotlightCardsBlock>(
  HOMEPAGE_BLOCK_IDS.SPOTLIGHT_CARDS,
);

export const isProductTilesBlock = createTypeGuard<ProductTilesBlock>(
  HOMEPAGE_BLOCK_IDS.PRODUCT_TILES,
);

export const isPrimeAdsBlock = createTypeGuard<PrimeAdsBlock>(
  HOMEPAGE_BLOCK_IDS.PRIME_ADS,
);

export const isNotificationBannerBlock =
  createTypeGuard<NotificationBannerBlock>(
    HOMEPAGE_BLOCK_IDS.NOTIFICATION_BANNER,
  );

export const isRecommendationTabsBlock =
  createTypeGuard<RecommendationTabsBlock>(
    HOMEPAGE_BLOCK_IDS.RECOMMENDATION_TABS,
  );

export const isCookiesBannerBlock = createTypeGuard<CookiesBannerBlock>(
  HOMEPAGE_BLOCK_IDS.COOKIES_BANNER,
);

export const isLoginBannerBlock = createTypeGuard<LoginBannerBlock>(
  HOMEPAGE_BLOCK_IDS.LOGIN_BANNER,
);

export const isHeroBannerBlock = createTypeGuard<HeroBannerBlock>(
  HOMEPAGE_BLOCK_IDS.HERO_BANNER,
);

export const isArticleCarouselBlock = createTypeGuard<ArticleCarouselBlock>(
  HOMEPAGE_BLOCK_IDS.ARTICLE_CAROUSEL,
);

export const isFooterAdsBannerBlock = createTypeGuard<FooterAdsBannerBlock>(
  HOMEPAGE_BLOCK_IDS.FOOTER_ADS_BANNER,
);

export const isLobSearchTabsBlock = createTypeGuard<LobSearchTabsBlock>(
  HOMEPAGE_BLOCK_IDS.LOB_SEARCH_TABS,
);

/**
 * Get typed block data by ID with type guard
 */
export function getBlockById<T extends HomepageBlock>(
  blocks: HomepageBlock[] | undefined,
  blockId: string,
  typeGuard: (block: HomepageBlock) => block is T,
): T | undefined {
  if (!blocks) return undefined;
  const block = blocks.find((b) => b.id === blockId);
  return block && typeGuard(block) ? block : undefined;
}

/**
 * Filter blocks by type using type guard
 */
export function filterBlocksByType<T extends HomepageBlock>(
  blocks: HomepageBlock[] | undefined,
  typeGuard: (block: HomepageBlock) => block is T,
): T[] {
  return blocks?.filter(typeGuard) ?? [];
}

/**
 * Quick accessors using constants to avoid passing type guards manually.
 */
export const getSpotlightCards = (blocks: HomepageBlock[] | undefined) =>
  getBlockById(
    blocks,
    HOMEPAGE_BLOCK_IDS.SPOTLIGHT_CARDS,
    isSpotlightCardsBlock,
  );

export const getProductTiles = (blocks: HomepageBlock[] | undefined) =>
  getBlockById(blocks, HOMEPAGE_BLOCK_IDS.PRODUCT_TILES, isProductTilesBlock);

export const getPrimeAds = (blocks: HomepageBlock[] | undefined) =>
  getBlockById(blocks, HOMEPAGE_BLOCK_IDS.PRIME_ADS, isPrimeAdsBlock);

export const getNotificationBanner = (blocks: HomepageBlock[] | undefined) =>
  getBlockById(
    blocks,
    HOMEPAGE_BLOCK_IDS.NOTIFICATION_BANNER,
    isNotificationBannerBlock,
  );

export const getRecommendationTabs = (blocks: HomepageBlock[] | undefined) =>
  getBlockById(
    blocks,
    HOMEPAGE_BLOCK_IDS.RECOMMENDATION_TABS,
    isRecommendationTabsBlock,
  );

export const getCookiesBanner = (blocks: HomepageBlock[] | undefined) =>
  getBlockById(blocks, HOMEPAGE_BLOCK_IDS.COOKIES_BANNER, isCookiesBannerBlock);

export const getLoginBanner = (blocks: HomepageBlock[] | undefined) =>
  getBlockById(blocks, HOMEPAGE_BLOCK_IDS.LOGIN_BANNER, isLoginBannerBlock);

export const getHeroBanner = (blocks: HomepageBlock[] | undefined) =>
  getBlockById(blocks, HOMEPAGE_BLOCK_IDS.HERO_BANNER, isHeroBannerBlock);

export const getArticleCarousel = (blocks: HomepageBlock[] | undefined) =>
  getBlockById(
    blocks,
    HOMEPAGE_BLOCK_IDS.ARTICLE_CAROUSEL,
    isArticleCarouselBlock,
  );

export const getFooterAdsBanner = (blocks: HomepageBlock[] | undefined) =>
  getBlockById(
    blocks,
    HOMEPAGE_BLOCK_IDS.FOOTER_ADS_BANNER,
    isFooterAdsBannerBlock,
  );

export const getLobSearchTabs = (blocks: HomepageBlock[] | undefined) =>
  getBlockById(
    blocks,
    HOMEPAGE_BLOCK_IDS.LOB_SEARCH_TABS,
    isLobSearchTabsBlock,
  );

export const isBottomNavigationBlock = createTypeGuard<BottomNavigationBlock>(
  HOMEPAGE_BLOCK_IDS.BOTTOM_NAVIGATION,
);

export const getBottomNavigation = (blocks: HomepageBlock[] | undefined) =>
  getBlockById(
    blocks,
    HOMEPAGE_BLOCK_IDS.BOTTOM_NAVIGATION,
    isBottomNavigationBlock,
  );
